import { isISwimlaneBase, ISwimlaneBase } from './SwimlaneStore'
import Dispatcher, { IAction, IActionPayload } from '../Dispatcher'

export interface ISwimlaneCreatePayload {
    boardId: number
}

export interface ISwimlanePayload extends ISwimlaneCreatePayload {
    swimlane: ISwimlaneBase
}

/**
 * Used when requesting a Swimlane to be moved.
 * May move a swimlane from anywhere to anywhere.
 */
export interface ISwimlaneMovePayload {
    from: { boardId: number; swimlaneId: number }
    to: { boardId: number; swimlaneId: number }
}

// Types associated with Swimlane Actions
const ADD_SWIMLANE = 'ADD_SWIMLANE'
const UPDATE_SWIMLANE = 'UPDATE_SWIMLANE'
const MOVE_SWIMLANE = 'MOVE_SWIMLANE'
const DELETE_SWIMLANE = 'DELETE_SWIMLANE'
const CREATE_SWIMLANE = 'CREATE_SWIMLANE'

/**
 * Typeguard for ISwimlaneCreatePayload Action.
 * Returns undefined if the action passed is not a ISwimlaneCreatePayload.
 * Otherwise will return true.
 * @param action Action to check
 */
function isSwimlaneCreateAction(
    action: IAction | IActionPayload<ISwimlaneCreatePayload>
): action is IActionPayload<ISwimlaneCreatePayload> {
    const payload = action as IActionPayload<ISwimlanePayload>
    // ensure we have a payload
    if (!payload.payload) return false
    // ensure we have a board id
    if (payload.payload.boardId === undefined) return false
    return true
}

/**
 * Typeguard for ISwimlanePayload Action.
 * Returns undefined if the action passed in is not a ISwimlanePayload.
 * Otherwise will return true.
 * @param action Action to check
 */
function isSwimlaneAction(
    action: IAction | IActionPayload<ISwimlanePayload>
): action is IActionPayload<ISwimlanePayload> {
    // ensure we have a board id
    if (!isSwimlaneCreateAction(action)) return false
    const payload = action as IActionPayload<ISwimlanePayload>
    // ensure we have a swimlane
    if (!payload.payload.swimlane) return false
    // ensure the swimlane is valid
    if (!isISwimlaneBase(payload.payload.swimlane)) return false
    return true
}

/**
 * Typeguard for IActionPayload<ISwimlaneMovePayload>
 * If true then anyting after invocation treats action.payload as a ISwimlaneMovePayload
 * otherwise on False it is treated as IAction
 * @param action action to check
 */
function isSwimlaneMoveAction(
    action: IAction | IActionPayload<ISwimlaneMovePayload>
): action is IActionPayload<ISwimlaneMovePayload> {
    const payload = action as IActionPayload<ISwimlaneMovePayload>
    // ensure we have a payload
    if (!payload.payload) return false
    // ensure we have from and to
    const { from, to } = payload.payload
    if (from === undefined || to === undefined) return false
    // ensure all properties are defined for both
    if (
        from.boardId === undefined ||
        from.swimlaneId === undefined ||
        to.boardId === undefined ||
        to.swimlaneId === undefined
    )
        return false
    return true
}

/**
 * Dispatches action to add a swimlane to a board
 * @param boardId ID of board to add swimlane to
 * @param swimlane Swimlane to add
 */
function addSwimlane(boardId: number, swimlane: ISwimlaneBase) {
    Dispatcher.dispatch({
        actionType: ADD_SWIMLANE,
        payload: {
            boardId: boardId,
            swimlane: swimlane,
        },
    } as IActionPayload<ISwimlanePayload>)
}

/**
 * Dispatches action to update a swimlane
 * @param boardId ID of board to add swimlane to
 * @param swimlane Updated swimlane
 */
function updateSwimlane(boardId: number, swimlane: ISwimlaneBase) {
    Dispatcher.dispatch({
        actionType: UPDATE_SWIMLANE,
        payload: {
            boardId: boardId,
            swimlane: swimlane,
        },
    } as IActionPayload<ISwimlanePayload>)
}

/**
 * Dispatches action to move swimlane position on a board.
 * Moves the swimlane identified by swimlaneId to the position of the toSwimlaneId
 * @param fromBoardId Board the from swimlane exists on
 * @param swimlaneId ID of the swimlane to be moved (from swimlane)
 * @param toBoardId Board to move the from swimlane to
 * @param toSwimlaneId ID of the swimlane in the toBoard to position the from swimlane relative to
 */
function moveSwimlane(
    fromBoardId: number,
    swimlaneId: number,
    toBoardId: number,
    toSwimlaneId: number
) {
    Dispatcher.dispatch({
        actionType: MOVE_SWIMLANE,
        payload: {
            from: { boardId: fromBoardId, swimlaneId: swimlaneId },
            to: { boardId: toBoardId, swimlaneId: toSwimlaneId },
        },
    } as IActionPayload<ISwimlaneMovePayload>)
}

/**
 * Dispatches action to delete a swimlane
 * @param boardId ID of board to add swimlane to
 * @param swimlane Swimlane to delete
 */
function deleteSwimlane(boardId: number, swimlane: ISwimlaneBase) {
    Dispatcher.dispatch({
        actionType: DELETE_SWIMLANE,
        payload: {
            boardId: boardId,
            swimlane: swimlane,
        },
    } as IActionPayload<ISwimlanePayload>)
}

/**
 * Dispatches action to create a swimlane
 * @param boardId Board this swimlane will belong to
 */
function createSwimlane(boardId: number) {
    Dispatcher.dispatch({
        actionType: CREATE_SWIMLANE,
        payload: {
            boardId,
        },
    } as IActionPayload<ISwimlaneCreatePayload>)
}

export {
    addSwimlane,
    updateSwimlane,
    deleteSwimlane,
    createSwimlane,
    moveSwimlane,
    ADD_SWIMLANE,
    UPDATE_SWIMLANE,
    MOVE_SWIMLANE,
    DELETE_SWIMLANE,
    CREATE_SWIMLANE,
    isSwimlaneCreateAction,
    isSwimlaneAction,
    isSwimlaneMoveAction,
}
